import { useEffect, useState, useRef } from "react";
import { AuthModel } from "../services/models";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { sessionManager } from "../utils/sessionManager";

export const useSessionCheck = () => {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const { isLoggedIn, toggleLogin } = useAuth();
  const location = useLocation();
  const checkIntervalRef = useRef(null);
  const isCheckingRef = useRef(false);

  const checkSession = async () => {
    // Don't check if already checking or not on a protected route
    if (isCheckingRef.current || !isLoggedIn) {
      return;
    }

    // Don't check on login/signup/forgot-password pages
    const publicRoutes = ["/login", "/signup", "/forgot-password"];
    if (publicRoutes.includes(location.pathname)) {
      return;
    }

    const token = AuthModel.getToken();
    if (!token) {
      setIsSessionExpired(true);
      toggleLogin(false);
      sessionManager.notifySessionExpired();
      return;
    }

    try {
      isCheckingRef.current = true;
      const result = await AuthModel.verifyToken();
      
      if (!result || result.status !== 200) {
        setIsSessionExpired(true);
        toggleLogin(false);
        AuthModel.logout();
        sessionManager.notifySessionExpired();
      }
    } catch (error) {
      // If token verification fails (401, 403, etc.), session is expired
      if (error.response?.status === 401 || error.response?.status === 403) {
        setIsSessionExpired(true);
        toggleLogin(false);
        AuthModel.logout();
        sessionManager.notifySessionExpired();
      }
    } finally {
      isCheckingRef.current = false;
    }
  };

  useEffect(() => {
    // Subscribe to session expiration events from axios interceptor
    const unsubscribe = sessionManager.subscribe(() => {
      setIsSessionExpired(true);
      toggleLogin(false);
    });

    // Check session when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isLoggedIn) {
        checkSession();
      }
    };

    // Check session periodically (every 5 minutes) when tab is active
    const startPeriodicCheck = () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      
      checkIntervalRef.current = setInterval(() => {
        if (document.visibilityState === "visible" && isLoggedIn) {
          checkSession();
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
    };

    // Initial check
    if (isLoggedIn) {
      checkSession();
      startPeriodicCheck();
    }

    // Listen for tab visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isLoggedIn, location.pathname]);

  // Reset session expiration state when user logs in
  useEffect(() => {
    if (isLoggedIn && isSessionExpired) {
      setIsSessionExpired(false);
      sessionManager.reset();
    }
  }, [isLoggedIn]);

  return { isSessionExpired, setIsSessionExpired };
};

